import { create } from "zustand";
import {
  challengeScoreBucket,
  trackChallengeCompleted,
  trackChallengeStarted,
} from "../lib/analytics";
import { CHALLENGE_LENGTH } from "../lib/dailyChallenge";
import { storage as appStorage, type TypedStorage } from "../lib/storage";

/**
 * The daily challenge's run state (GRA-139): which day is open, how far into
 * it the player is, whether this attempt is the recorded one, and every
 * finished day's result. Kept apart from the Loop store (challengeStore.ts),
 * which owns only the Round lifecycle: this is the bookkeeping the Loop's
 * `run` callbacks delegate to, and it is what the progress indicator and the
 * end screen read.
 *
 * Two storage keys, both self-invalidating rather than per-date:
 *
 * - `challenge:run` holds at most one in-progress recorded attempt, stamped
 *   with its date. A run left behind by an earlier day is ignored on sight
 *   (its date no longer matches), so nothing has to enumerate or sweep keys.
 * - `challenge:results` maps date → result, capped so a year of play cannot
 *   grow the stored payload without bound.
 */

const RUN_KEY = "challenge:run";
const RESULTS_KEY = "challenge:results";

/** How many days of results are kept; older days fall off. */
export const RESULTS_CAP = 60;

/** A finished recorded attempt. */
export type ChallengeResult = {
  date: string;
  /** Outcomes in play order. */
  outcomes: boolean[];
  correct: number;
  total: number;
  completedAt: number;
  /**
   * The Round ids the attempt played, in play order (GRA-145). Optional
   * because results recorded before pinning existed do not carry them; a
   * practice replay of such a day falls back to picking the day fresh.
   */
  ids?: string[];
};

/** An in-progress recorded attempt, as persisted. */
export type StoredRun = {
  date: string;
  outcomes: boolean[];
  /**
   * The Round ids this run pinned when it started (GRA-145). Optional for the
   * same reason as on a result: a run stored before pinning existed resumes
   * on freshly picked ids rather than crashing.
   */
  ids?: string[];
};

export interface ChallengeRunState {
  /** The local date key this run belongs to, or "" before the day is opened. */
  date: string;
  /** How many Rounds the day's run holds (the bank can be shorter than ten). */
  total: number;
  /** The Round ids the open run plays, in play order (GRA-145). */
  ids: string[];
  /**
   * True when this run is a replay of an already-recorded day, so nothing it
   * does is kept. Set only by `replay`: simply *returning* to a finished day
   * shows its recorded result rather than quietly starting a practice run.
   */
  practice: boolean;
  /** Outcomes of the run in progress, oldest first. */
  outcomes: boolean[];
  /** The recorded (first) attempt for `date`, once it is complete. */
  recorded: ChallengeResult | null;
  /**
   * Whether the *practice* run in progress has already reported its
   * completion. In memory only and never persisted: it guards one analytics
   * event against a repeated `record` at the run's last index, and a
   * recorded attempt uses `recorded` for the same job.
   */
  announced: boolean;

  /**
   * The ids `date` is already committed to, if any: the recorded result's for
   * a finished day, else an in-progress run's. Read *before* `open`, since it
   * is what the Loop resolves its order against (GRA-145). Null when the day
   * is new, or when what is stored predates pinning.
   */
  pinnedIds: (date: string) => string[] | null;
  /**
   * Open `date` on the Round ids it will play, in play order: resume a stored
   * attempt, show a finished day's recorded result, or start fresh.
   * Idempotent for a day that is already open, so re-entering the Loop (or
   * the re-hydrate `replay` triggers) never clobbers the run in progress.
   */
  open: (date: string, ids: string[]) => void;
  /** Fold in one completed Round: its 0-based index and whether it was right. */
  record: (index: number, correct: boolean) => void;
  /** Replay the open day for practice, leaving the recorded result alone. */
  replay: () => void;
}

/** Newest days first, capped, so a long-lived profile stays small. */
function capResults(
  results: Record<string, ChallengeResult>
): Record<string, ChallengeResult> {
  const dates = Object.keys(results).sort().reverse().slice(0, RESULTS_CAP);
  return Object.fromEntries(dates.map((date) => [date, results[date]]));
}

export function createChallengeRunStore(deps: { storage?: TypedStorage } = {}) {
  const store = deps.storage ?? appStorage;
  const readResults = () =>
    store.get<Record<string, ChallengeResult>>(RESULTS_KEY, {});

  return create<ChallengeRunState>((set, get) => ({
    date: "",
    total: CHALLENGE_LENGTH,
    ids: [],
    practice: false,
    outcomes: [],
    recorded: null,
    announced: false,

    pinnedIds: (date) => {
      const recorded = readResults()[date];
      if (recorded?.ids?.length) return recorded.ids;
      const stored = store.get<StoredRun | null>(RUN_KEY, null);
      if (stored && stored.date === date && stored.ids?.length)
        return stored.ids;
      return null;
    },

    open: (date, ids) => {
      const total = ids.length;
      const state = get();
      if (state.date === date) {
        // Same day, already open: the run itself stands. Only its shape can
        // have moved underneath it (a slot refilled from a redeployed bank,
        // or a bank that loaded shorter than expected).
        if (state.total !== total || state.ids.join() !== ids.join())
          set({ total, ids });
        return;
      }

      const recorded = readResults()[date] ?? null;
      if (recorded) {
        // The day is done. Open it *on its result*, not on a silent practice
        // run: a player coming back wants to see what they got, and a replay
        // is something they ask for. Practice only starts at `replay`.
        set({
          date,
          total,
          ids,
          recorded,
          practice: false,
          announced: false,
          outcomes: recorded.outcomes.slice(0, total),
        });
        return;
      }

      // Resume only a stored run for *this* day. Anything else (yesterday's
      // abandoned run) is dead and simply ignored.
      const stored = store.get<StoredRun | null>(RUN_KEY, null);
      const resumable =
        stored && stored.date === date ? stored.outcomes.slice(0, total) : [];
      // A fresh run: nothing stored yet for today, so this is the moment the
      // recorded attempt actually begins (GRA-149). A reload mid-run finds
      // `resumable` non-empty and reports nothing, since the attempt already
      // started earlier.
      if (resumable.length === 0) trackChallengeStarted({ recorded: true });
      set({
        date,
        total,
        ids,
        recorded: null,
        practice: false,
        announced: false,
        outcomes: resumable,
      });
      // Persist the ids as soon as the day opens, not at the first answer: a
      // run pinned only once a Round has been committed would still shift
      // under a player who reloads before guessing. This is also where a run
      // stored before pinning existed (or one whose slot was refilled) picks
      // up the ids it is actually playing.
      if (total > 0)
        store.set(RUN_KEY, {
          date,
          outcomes: resumable,
          ids,
        } satisfies StoredRun);
    },

    record: (index, correct) => {
      const { outcomes, practice, date, total, ids, recorded } = get();
      // The recorded attempt is already finished, so there is nothing left to
      // record. Without this, a second `record` at the run's last index (a
      // double commit, a re-render reaching the Loop's callback twice) would
      // rebuild the same completed run and fire a second
      // `challenge_completed` for one attempt. A reload is already safe by
      // another route: `open` takes the recorded branch and never reaches
      // here. Practice replays are unaffected, since they never set
      // `recorded`.
      if (!practice && recorded) return;
      // Index-addressed rather than appended, so recording the same Round
      // twice (a double commit, a re-render) overwrites instead of growing
      // the run past its length.
      const next = [...outcomes.slice(0, index), correct];
      set({ outcomes: next });

      if (practice) {
        // Same once-per-run rule as the recorded branch below, but a practice
        // replay stores nothing that could stand in for "already finished",
        // so it needs its own in-memory latch. Cleared by `replay`, which is
        // the only way a second practice run can start.
        if (next.length >= total && !get().announced) {
          set({ announced: true });
          trackChallengeCompleted({
            score: challengeScoreBucket(
              next.filter(Boolean).length,
              next.length
            ),
            recorded: false,
          });
        }
        return;
      }

      if (next.length >= total) {
        const result: ChallengeResult = {
          date,
          outcomes: next,
          correct: next.filter(Boolean).length,
          total: next.length,
          completedAt: Date.now(),
          ids: ids.slice(0, next.length),
        };
        store.set(
          RESULTS_KEY,
          capResults({ ...readResults(), [date]: result })
        );
        // The recorded attempt is complete, so there is nothing left to
        // resume: drop the in-progress key rather than leave it to be
        // ignored later.
        store.remove(RUN_KEY);
        set({ recorded: result });
        trackChallengeCompleted({
          score: challengeScoreBucket(result.correct, result.total),
          recorded: true,
        });
        return;
      }

      store.set(RUN_KEY, { date, outcomes: next, ids } satisfies StoredRun);
    },

    replay: () => {
      set({ practice: true, outcomes: [], announced: false });
      trackChallengeStarted({ recorded: false });
    },
  }));
}

export const useChallengeRunStore = createChallengeRunStore();
